import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, input, output } from '@angular/core';
import { ifsStyles } from '../../../../../styles/moto-ifs/iframe-styles';
import { I18nService } from '../../../../core/services/i18n.service';
import { RuntimeConfigService } from '../../../../core/services/runtime-config.service';
import type { PagateqIfsInstance, PaymentTypeStatus } from '../../../interfaces/ifs-moto/pagateq-ifs.types';

@Component({
  selector: 'sps-ifs-credit-card',
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ifs-credit-card.component.html',
})
export class IfsCreditCardComponent implements AfterViewInit {

  i18n = inject(I18nService);

  /**
   * ifs-configuration
   * @private
   */
  private readonly runtimeConfig = inject(RuntimeConfigService);
  /**
   * total cleanup on destroy
   * @private
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Inputs
   */
  readonly paymentTypeStatus = input.required<PaymentTypeStatus>();           // TEST | LIVE → Script-URL
  readonly clientSession = input.required<string>();                                    // vom Dialog (nach /init)
  readonly clientConfiguration = input.required<string>();                              // vom Dialog (nach /init)

  /**
   * Outputs
   */
  readonly ready = output<PagateqIfsInstance>();
  readonly errored = output<unknown>();
  readonly brandChanged   = output<any>();
  readonly formValidationChanged   = output<any>();

  /**
   * Pagateq IFrame-service instance
   * @private
   */
  private instance?: PagateqIfsInstance;

  /**
   * placeholder for card holder
   */
  private holderPlaceholder: string = this.i18n.i18nLng()['PLACEHOLDER'];

  /**
   * Load Script async after DOM initialization
   */
  async ngAfterViewInit(): Promise<void> {
    try {
      const scriptUrl = this.runtimeConfig.resolveIfsScriptUrl(this.paymentTypeStatus());
      await this.loadScript(scriptUrl);
      if (!window.integration) {
        throw new Error(this.i18n.i18nLng()['SCRIPTNOTAVAILABLE']);
      }
      /**
       * Backend liefert clientSession + clientConfiguration (base64)
       * → diese kommen als Inputs in die Component (statt selbst gebauter sessionId)
       */
      this.instance = await window.integration.initialize({
        clientSession: this.clientSession(),
        clientConfiguration: this.clientConfiguration(),
        type: 'creditCard',
        fields: {
          holder: { selector: '#ifs-holder', required: true, placeholder: this.holderPlaceholder },
          number: { selector: '#ifs-pan', placeholder: '4111 1111 1111 1111' },
          expiry: { selector: '#ifs-expiry', placeholder: 'MM/YY' },
          code:   { selector: '#ifs-cvv' },
          // holder nur nötig, wenn 3DS 2.0 aktiv
        },
        styles: ifsStyles,
        configuration: { tds20: false },
      });
      this.ready.emit(this.instance);
      /**
       * Brand changed
       */
      this.instance.on('cardBrandChange', (p) =>
        this.brandChanged.emit(p as any),
      );
      /**
       * Form-Validation changed use to activate the payment button
       */
      this.instance.on('formValidationChange', (p) =>
        this.formValidationChanged.emit(p as any),
      );
      /**
       * Kein destroy() — Cleanup ggf. via clearFields() + DOM entfernen
       */
      this.destroyRef.onDestroy(() => this.instance?.clearFields());
    } catch (err) {
      this.errored.emit(err);
    }
  }

  /**
   * Pagateq ifs Script-Loader
   * Anmerkung MK: Das Script muss immer zuerst entfernt werden, da es ja im document
   * injected wird und somit nach der injection vorhanden bleibt.
   * damit zwischen TEST und LIVE-Zahlungen fehlerfrei gewechselt werden kann
   * muss das letzte script, falls vorhanden, gelöscht werden, bevor das neue
   * script wieder injected werden kann
   * @param src
   * @private
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      /**
       * delete existing scripts
       */
      document
      .querySelectorAll<HTMLScriptElement>('script[data-pagateq-ifs]')
      .forEach(el => el.parentNode?.removeChild(el));
      /**
       * reset the window namespace
       */
      delete (window as any).integration;
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      // s.crossOrigin = 'anonymous';     // no cross-origin -> dsv proxy is blocking
      s.dataset['pagateqIfs'] = 'true';   // generates data-pagateq-ifs="true" in <script> tag in DOM
      s.onload = () => resolve();
      s.onerror = () => reject(
        new Error(this.i18n.i18nLng()['LOADSCRIPTERROR'] + ': ' + src)
      );
      document.head.appendChild(s);
    });
  }

}
